import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { tap } from 'rxjs/operators';
import { makeStyles } from 'tss-react/mui';

import { ConfirmDeleteDialog } from '~/components/confirm-delete-dialog';
import { DetailedErrorDialog } from '~/components/detailed-error-dialog';
import ExistingExports from '~/components/existing-exports';
import type { GenericDetailedError } from '~/errors/GenericDetailedError';
import { default as NewExportDialog, type NewExportDialogRaw } from '~/features/exports/new-export-dialog';
import * as authService from '~/services/authentication';
import { type ExportEntry, ExportService } from '~/services/exports';
import { AuthStatus, Roles } from '~/types/types';
import { NFRDialog, NFRDialogRaw } from '~/app/routes/NFRDialog';

const useStyles = makeStyles()({
  heading: {
    marginTop: '16px',
  },

  createButton: {
    width: '100%',
    marginTop: '16px',
    marginBottom: '16px',
    whiteSpace: 'nowrap',
  },

  description: {
    marginTop: '16px',
    marginBottom: '16px',
  },
});

type ExportsPageProps = {
  classes: ReturnType<typeof useStyles>['classes'];
};

interface ExportsPageState {
  snackbar: { show: boolean; message: string };
}

export const AcceptableRoles: Readonly<Roles[]> = [Roles.ViewExport];
let Sekretess = false;
class ExportsPage extends React.Component<ExportsPageProps, ExportsPageState> {
  dialogRefNew: React.RefObject<NewExportDialogRaw>;
  dialogRefNFR: React.RefObject<NFRDialogRaw>;
  confirmDeleteDialogRef: React.RefObject<ConfirmDeleteDialog>;
  errorDialogRef: React.RefObject<DetailedErrorDialog>;

  constructor(props: ExportsPageProps) {
    super(props);
    this.dialogRefNew = React.createRef<NewExportDialogRaw>();
    this.dialogRefNFR = React.createRef<NFRDialogRaw>();
    this.confirmDeleteDialogRef = React.createRef<ConfirmDeleteDialog>();
    this.errorDialogRef = React.createRef<DetailedErrorDialog>();
    this.state = {
      snackbar: {
        show: false,
        message: '',
      },
    };
    this.isAuthorized();
  }

  handleCreateButtonClickXML = () => {
    if (this.dialogRefNew.current) {
      this.dialogRefNew.current.openDialog();
    }
  };

  handleButtonClickNFR = () => {
    if (this.dialogRefNFR.current) {
      this.dialogRefNFR.current.openDialog();
    }
  };

  exportStarted = () => {
    this.setState({ snackbar: { show: true, message: 'Export påbörjad.' } });
  };

  importStarted = () => {
    this.setState({
      snackbar: { show: true, message: 'Importen påbörjad.' },
    });
  };

  deleteStarted = () => {
    this.setState({
      snackbar: { show: true, message: 'borttagningen påbörjad.' },
    });
  };

  dialogError = (error: GenericDetailedError) => {
    if (this.errorDialogRef.current) {
      this.errorDialogRef.current.openDialog(error);
    }
  };
  isAuthorized() {
    authService
      .getAuthStatusObs([Roles.Sekretess])
      .pipe(
        tap((authStatus) => {
          Sekretess = authStatus === AuthStatus.Authorized;
        })
      )
      .subscribe();
  }
  confirmDelete = (exported: ExportEntry): Promise<boolean> => {
    if (this.confirmDeleteDialogRef.current) {
      return this.confirmDeleteDialogRef.current.openDialog(exported).then((res) => {
        if (!res) {
          return res;
        }

        return ExportService.deleteExport(exported.id)
          .then(() => res)
          .catch((err) => {
            this.dialogError(err);
            return false;
          });
      });
    }

    return Promise.resolve(false);
  };

  render() {
    const { classes } = this.props;
    return (
      <>
        <Grid container item xs={12} md={10} lg={8} spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.heading} variant="h4">
              Exporter
            </Typography>
          </Grid>
          <Grid className={classes.description} item xs={12} md={8} lg={8}>
            <Typography>Nedan finner du tidigare gjorda exporter:</Typography>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <Button
              className={classes.createButton}
              onClick={this.handleCreateButtonClickXML}
              variant="contained"
              color="primary"
            >
              General export
            </Button>
          </Grid>
          <Grid item={true} xs={12} md={2} lg={2}>
            <Button
                className={classes.createButton}
                onClick={this.handleButtonClickNFR}
                variant="contained"
                color="primary"
            >
              NFR
            </Button>
          </Grid>
            <Grid item xs={12}>
            <ExistingExports onError={this.dialogError} onDelete={this.confirmDelete} />
          </Grid>
          <NewExportDialog ref={this.dialogRefNew} onDone={this.exportStarted} onError={this.dialogError} />
          <NFRDialog
              ref={this.dialogRefNFR}
              Sekretess={Sekretess}
              exportStarted={this.exportStarted}
              importDone={this.importStarted}
              deleteDone={this.deleteStarted}
              onError={this.dialogError}
          />
        </Grid>
        <DetailedErrorDialog ref={this.errorDialogRef} />
        <ConfirmDeleteDialog ref={this.confirmDeleteDialogRef} />
        <Snackbar
          open={this.state.snackbar.show}
          message={this.state.snackbar.message}
          autoHideDuration={6000}
          onClose={() => this.setState({ snackbar: { show: false, message: '' } })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </>
    );
  }
}

const Wrapper = (props: Omit<ExportsPageProps, 'classes'>) => {
  const { classes } = useStyles();

  return <ExportsPage classes={classes} {...props} />;
};

export default Wrapper;
